import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from "@mui/material";
import React, {useCallback, useEffect, useState} from "react";
import {CookService} from "../../services/CookServices";
import EventBus from "../../EventBus";
import {EventBusEventsEnum} from "../../enums/EventBusEventsEnum";
import {CustomSnackbarTypes} from "../Common/CustomSnackBar";
import AreaService from "../../services/AreaService";
import {SlcServiceCookMappingEnum} from "../../enums/SlcServiceCookMappingEnum";
import {setCustomSelectOpen} from "../../store/reducers/customSelect";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import ResourceSelectDialog from "../Common/ResourceSelectDialog";
import AreaList from "../Area/AreaList";
import AddMultipleSlcsDialog from "../Common/AddMultipleSlcsDialog";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import {UserGroupsEnum} from "../../enums/UserGroupsEnum";
import CommonListTable, {CommonListColumn} from "../Common/CommonListTable";

type CookSLCProps = {
    cookId: number
}
const cookService = new CookService();
const areaService = new AreaService();

const CookSLC = (props: CookSLCProps) => {

    const dispatch = useAppDispatch();
    const userDetails = useAppSelector((state) => state.userDetails.value);
    const groupsArray: string[] = userDetails.adminUser.accessGroups;
    const isAdmin = GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN]);

    let [slots, setSlots] = React.useState<any>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    // 'active' | 'inactive' | null — locks selection to one type
    const [selectionMode, setSelectionMode] = useState<'active' | 'inactive' | null>(null);

    const [openMappingTypeDialog, setOpenMappingTypeDialog] = useState<boolean>(false);
    const [mappingType, setMappingType] = useState<SlcServiceCookMappingEnum | undefined>(undefined);
    const [openAddMultipleDialog, setOpenAddMultipleDialog] = useState<boolean>(false);

    let [allServices, setAllServices] = useState<any>([]);
    const [selectedService, setSelectedService] = useState<string>('');
    const [openAreaList, setOpenAreaList] = useState(false);

    const activeSlots = slots.filter((s: any) => !s.deletedAt);
    const inactiveSlots = slots.filter((s: any) => !!s.deletedAt);

    const handleSelectArea = () => {
        setOpenAreaList(true);
        dispatch(setCustomSelectOpen({ open: true }));
    };

    const handleClose = (recordData: any) => {
        setOpenAreaList(false);
        if (!mappingType) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Select Slc mapping type",
                type: CustomSnackbarTypes.ERROR,
            });
        }
        if (recordData && mappingType) {
            areaService.addCooksToSlc({
                referenceId: recordData?.id,
                referenceModelName: 'AREA',
                serviceId: Number(selectedService),
                cookId: props?.cookId,
                mappingType: mappingType,
            }).then(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Added Successfully",
                    type: CustomSnackbarTypes.SUCCESS,
                });
                setOpenMappingTypeDialog(false);
                getCookSlots();
            });
        } else {
            setOpenMappingTypeDialog(false);
        }
    };

    const getCookSlots = useCallback(() => {
        setLoading(true);
        cookService.getAllDailyCooksSlc(1000, 0, {cookId: props?.cookId}).then((res: any) => {
            setLoading(false);
            setSlots([...res?.data]);
            setSelectedIds([]);
            setSelectionMode(null);
        })
    }, [props?.cookId]);

    useEffect(() => {
        if(!props?.cookId) return;
        getCookSlots();
    }, [getCookSlots, props?.cookId]);

    useEffect(() => {
        if(!openMappingTypeDialog) return;
        areaService.getAllServices().then((res) => setAllServices(res));
    }, [openMappingTypeDialog]);

    const handleDeactivate = (id: number) => {
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            title: "Are you sure?",
            message: "Please confirm to deactivate this SLC mapping",
            successLabel: "Confirm",
            failureLabel: "Cancel",
            onClose: (confirmed: boolean) => {
                if (confirmed) {
                    areaService.deactivateSlcCookMapping(id).then(() => {
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                            message: "Deactivated Successfully",
                            type: CustomSnackbarTypes.SUCCESS,
                        });
                        getCookSlots();
                    });
                }
            },
        });
    };

    const handleActivate = (id: number) => {
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            title: "Are you sure?",
            message: "Please confirm to activate this SLC mapping",
            successLabel: "Confirm",
            failureLabel: "Cancel",
            onClose: (confirmed: boolean) => {
                if (confirmed) {
                    areaService.activateSlcCookMapping(id).then(() => {
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                            message: "Activated Successfully",
                            type: CustomSnackbarTypes.SUCCESS,
                        });
                        getCookSlots();
                    });
                }
            },
        });
    };

    const handleBulkDeactivate = () => {
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            title: "Are you sure?",
            message: `Deactivate ${selectedIds.length} selected SLC mapping(s)?`,
            successLabel: "Confirm",
            failureLabel: "Cancel",
            onClose: async (confirmed: boolean) => {
                if (confirmed) {
                    await Promise.all(selectedIds.map(id => areaService.deactivateSlcCookMapping(id)));
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: `${selectedIds.length} mapping(s) deactivated`,
                        type: CustomSnackbarTypes.SUCCESS,
                    });
                    getCookSlots();
                }
            },
        });
    };

    const handleBulkActivate = () => {
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            title: "Are you sure?",
            message: `Activate ${selectedIds.length} selected SLC mapping(s)?`,
            successLabel: "Confirm",
            failureLabel: "Cancel",
            onClose: async (confirmed: boolean) => {
                if (confirmed) {
                    await Promise.all(selectedIds.map(id => areaService.activateSlcCookMapping(id)));
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: `${selectedIds.length} mapping(s) activated`,
                        type: CustomSnackbarTypes.SUCCESS,
                    });
                    getCookSlots();
                }
            },
        });
    };

    const toggleSelect = (id: number, isDeactivated: boolean) => {
        const rowMode = isDeactivated ? 'inactive' : 'active';
        if (selectedIds.includes(id)) {
            const next = selectedIds.filter(i => i !== id);
            setSelectedIds(next);
            if (next.length === 0) setSelectionMode(null);
        } else {
            if (selectionMode !== null && selectionMode !== rowMode) return; // locked to other type
            setSelectedIds(prev => [...prev, id]);
            setSelectionMode(rowMode);
        }
    };

    const toggleSelectAllActive = () => {
        const activeIds = activeSlots.map((s: any) => s.id);
        const allActiveSelected = activeIds.every((id: number) => selectedIds.includes(id));
        if (allActiveSelected) {
            setSelectedIds([]);
            setSelectionMode(null);
        } else {
            setSelectedIds(activeIds);
            setSelectionMode('active');
        }
    };

    const toggleSelectAllInactive = () => {
        const inactiveIds = inactiveSlots.map((s: any) => s.id);
        const allInactiveSelected = inactiveIds.every((id: number) => selectedIds.includes(id));
        if (allInactiveSelected) {
            setSelectedIds([]);
            setSelectionMode(null);
        } else {
            setSelectedIds(inactiveIds);
            setSelectionMode('inactive');
        }
    };

    const activeIds = activeSlots.map((s: any) => s.id);
    const inactiveIds = inactiveSlots.map((s: any) => s.id);
    const allActiveSelected = activeIds.length > 0 && activeIds.every((id: number) => selectedIds.includes(id));
    const someActiveSelected = activeIds.some((id: number) => selectedIds.includes(id)) && !allActiveSelected;
    const allInactiveSelected = inactiveIds.length > 0 && inactiveIds.every((id: number) => selectedIds.includes(id));
    const someInactiveSelected = inactiveIds.some((id: number) => selectedIds.includes(id)) && !allInactiveSelected;

    const columns: CommonListColumn<any>[] = [
        {
            label: '',
            stopRowClick: true,
            render: (each) => {
                const isDeactivated = !!each?.deletedAt;
                const isSelected = selectedIds.includes(each?.id);
                const rowMode = isDeactivated ? 'inactive' : 'active';
                const isDisabled = selectionMode !== null && selectionMode !== rowMode;
                return (
                    <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => toggleSelect(each?.id, isDeactivated)}
                    />
                );
            },
        },
        { label: 'Id', field: 'id' },
        {
            label: 'SLC Name',
            render: (each) => {
                const isDeactivated = !!each?.deletedAt;
                if (each?.referenceModelName === 'AREA') {
                    return (
                        <Link href={`/#/Areas/${each?.referenceId}/show`}>
                            <Typography color={isDeactivated ? 'error' : 'inherit'}>
                                {each?.slcName}
                            </Typography>
                        </Link>
                    );
                }
                return (
                    <Typography color={isDeactivated ? 'error' : 'inherit'}>
                        {each?.slcName}
                    </Typography>
                );
            },
        },
        { label: 'Reference Model Name', field: 'referenceModelName' },
        { label: 'Service Name', field: 'serviceName' },
        { label: 'Mapping Type', field: 'mappingType' },
        {
            label: 'Status',
            render: (each) => (
                <Chip
                    label={!!each?.deletedAt ? 'Deactivated' : 'Active'}
                    color={!!each?.deletedAt ? 'error' : 'success'}
                    size={'small'}
                />
            ),
        },
        {
            label: 'Deleted By',
            render: (each) => each?.deletedById ?? '-',
        },
        {
            label: 'Activate / Deactivate',
            stopRowClick: true,
            render: (each) => !!each?.deletedAt ? (
                <Button size={'small'} color={'success'} variant={'contained'} onClick={() => handleActivate(each?.id)}>
                    Activate
                </Button>
            ) : (
                <Button size={'small'} color={'warning'} variant={'contained'} onClick={() => handleDeactivate(each?.id)}>
                    Deactivate
                </Button>
            ),
        },
        ...(isAdmin ? [{
            label: 'Remove',
            stopRowClick: true,
            render: (each: any) => (
                <Button
                    size={'small'}
                    color={'error'}
                    variant={'contained'}
                    onClick={() => {
                        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                            title: "Are you sure?",
                            message: "Please confirm to remove the cook",
                            successLabel: "Confirm",
                            failureLabel: "Cancel",
                            onClose: (confirmed: boolean) => {
                                if (confirmed) {
                                    areaService.removeSlcCooks({
                                        referenceId: each?.referenceId,
                                        referenceModelName: each?.referenceModelName,
                                        serviceId: each?.serviceId,
                                        cookId: each?.cookId,
                                    }).then(() => {
                                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                            message: "Removed Successfully",
                                            type: CustomSnackbarTypes.SUCCESS,
                                        });
                                        getCookSlots();
                                    });
                                }
                            },
                        });
                    }}
                >
                    Remove
                </Button>
            ),
        } as CommonListColumn<any>] : []),
    ];

    return (
        <Box width={'100%'} mt={2}>
            {openAreaList && (
                <ResourceSelectDialog name={"Areas"} list={AreaList} onClose={handleClose} />
            )}
            <AddMultipleSlcsDialog
                open={openAddMultipleDialog}
                callback={(data: any) => {
                    setOpenAddMultipleDialog(false);
                    if(data) getCookSlots();
                }}
                cookId={props?.cookId}
            />

            {/* Toolbar */}
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                <Box display={'flex'} gap={1} alignItems={'center'}>
                    {selectedIds.length > 0 && selectionMode === 'active' && (
                        <Button size={'small'} color={'warning'} variant={'contained'} onClick={handleBulkDeactivate}>
                            {`Deactivate (${selectedIds.length})`}
                        </Button>
                    )}
                    {selectedIds.length > 0 && selectionMode === 'inactive' && (
                        <Button size={'small'} color={'success'} variant={'contained'} onClick={handleBulkActivate}>
                            {`Activate (${selectedIds.length})`}
                        </Button>
                    )}
                    {selectedIds.length > 0 && (
                        <Typography variant={'caption'} color={'text.secondary'}>
                            {`${selectedIds.length} selected`}
                        </Typography>
                    )}
                </Box>
                <Box display={'flex'} gap={3}>
                    <Button
                        color={'primary'}
                        variant={'contained'}
                        onClick={() => {
                            setOpenMappingTypeDialog(true);
                            setMappingType(undefined);
                            setSelectedService('');
                        }}
                    >
                        Add SLC
                    </Button>
                    <Button color={'primary'} variant={'contained'} onClick={() => setOpenAddMultipleDialog(true)}>
                        Add Multiple Slc's
                    </Button>
                </Box>
            </Box>

            <CommonListTable
                columns={columns}
                data={slots}
                loading={loading}
                emptyText="No SLC found"
            />

            {/* Select all active / inactive — below the table */}
            {slots.length > 0 && (
                <Box display={'flex'} gap={2} alignItems={'center'} mt={1} px={1}>
                    {activeSlots.length > 0 && (
                        <Box display={'flex'} alignItems={'center'} gap={0.5}>
                            <Checkbox
                                size={'small'}
                                checked={allActiveSelected}
                                indeterminate={someActiveSelected}
                                disabled={selectionMode === 'inactive'}
                                onChange={toggleSelectAllActive}
                            />
                            <Typography variant={'caption'}>Select all active</Typography>
                        </Box>
                    )}
                    {inactiveSlots.length > 0 && (
                        <Box display={'flex'} alignItems={'center'} gap={0.5}>
                            <Checkbox
                                size={'small'}
                                checked={allInactiveSelected}
                                indeterminate={someInactiveSelected}
                                disabled={selectionMode === 'active'}
                                onChange={toggleSelectAllInactive}
                            />
                            <Typography variant={'caption'}>Select all inactive</Typography>
                        </Box>
                    )}
                </Box>
            )}

            <Dialog open={openMappingTypeDialog} onClose={() => { setOpenMappingTypeDialog(false) }}>
                <DialogTitle>Select the Following</DialogTitle>
                <DialogContent sx={{width: 400}}>
                    <FormControl fullWidth>
                        <InputLabel id="mapping-type-label">Select Mapping Type</InputLabel>
                        <Select
                            labelId="mapping-type-label"
                            id="mapping-type"
                            value={mappingType}
                            label="Mapping Type"
                            onChange={(event: SelectChangeEvent) => {
                                setMappingType(event?.target?.value as SlcServiceCookMappingEnum)
                            }}
                        >
                            {Object.values(SlcServiceCookMappingEnum).map((value) => (
                                <MenuItem value={value}>{value}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Select Service Type</InputLabel>
                        <Select
                            value={selectedService}
                            label="Select Service Type"
                            onChange={(event: SelectChangeEvent) => {
                                setSelectedService(event?.target?.value)
                            }}
                        >
                            {allServices?.map((eachService: {id: number; name: string}) => (
                                <MenuItem value={eachService?.id}>{eachService?.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button variant={'contained'}
                        onClick={() => {
                            if(!mappingType) {
                                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                    message: "Select Mapping Type",
                                    type: CustomSnackbarTypes.ERROR,
                                });
                                return;
                            }
                            if(!selectedService) {
                                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                    message: "Select Service Type",
                                    type: CustomSnackbarTypes.ERROR,
                                });
                                return;
                            }
                            handleSelectArea();
                        }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
export default CookSLC;
